import createChangedSpellEntry from '../helpers/createChangedSpellEntry.js'
import hasTempId from '../helpers/hasTempId.js'
import isSpellChanged from '../helpers/isSpellChanged.js'
import isEmpty from '../helpers/isEmpty.js'

function getSpellId(spell) {
  return (
    spell.character_spell_id ||
    spell.character_rune_spell_id ||
    spell.character_item_spell_id ||
    spell.id ||
    null
  )
}

function getSpellRuneMetadata(spell) {
  return {
    character_rune_name: spell?.character_rune_name ?? '',
    casting_rune_name: spell?.casting_rune_name ?? '',
    secondary_rune_name: spell?.secondary_rune_name ?? '',
  }
}

//---------------------
// track spell changes
//---------------------
export default function trackSpellChanges({
  change = null,
  spell,
  original_spells,
  changed_spells,
  character_id,
  delete: del = false,
} = {}) {
  console.log('===================================================')
  console.log('IN trackSpellChanges')
  console.log('spell name = ', spell.name)
  console.log('spell = ', window.$deepCopy(spell))
  console.log('change = ', window.$deepCopy(change))
  console.log('original_spells = ', window.$deepCopy(original_spells))
  console.log('changed_spells = ', window.$deepCopy(changed_spells))
  console.log('delete = ', del)
  console.log('----------------------------------')
  //---------------------
  // check args received
  //---------------------
  // Normalise arrays so callers can safely pass null/undefined
  // (common when creating a brand new character with no spells yet).
  if (!Array.isArray(original_spells)) {
    original_spells = []
  }
  if (!Array.isArray(changed_spells)) {
    changed_spells = []
  }

  if (isEmpty(spell)) {
    return changed_spells
  }
  /*
  -------------------------------------------------------------------------------------------------------------------------
   CHANGED ARRAY    ORIGINAL ARRAY     PARM ACTION         CHANGED ARRAY ACTION
  ---------------  ----------------   ------------         ---------------------
  A.    No               No               ADD              add (new character spell)          
  B.    No               Yes              UPDATE           add existing character spell update
  C.    Yes              Yes              UPDATE/REMOVE    further character spell update or reverting to original)
  D.    Yes              No               ADD              update same ADD entry (further updates having flagged addition)
  -------------------------------------------------------------------------------------------------------------------------
  */
  let spell_changed_parm = {}
  //------------------------------------------------------------------
  // determine logical spell type for routing (character / item / rune)
  //------------------------------------------------------------------
  let endpointType = 'character'

  // Prefer explicit source label when present (Character / Item / Rune)
  if (spell.source) {
    const src = spell.source.toString().toLowerCase()
    if (src === 'item') {
      endpointType = 'item'
    } else if (src === 'rune') {
      endpointType = 'rune'
    }
  } else if (spell.character_item != null) {
    // New or local-only spells may not yet have a source label but
    // will still carry character_item / character_rune fields.
    endpointType = 'item'
  } else if (spell.character_rune != null) {
    endpointType = 'rune'
  }
  //---------------------------------------------------------------------------------
  // find spell in change array (using id if existing spell - or temp id for new spell)
  //---------------------------------------------------------------------------------
  const spellId = getSpellId(spell)
  const changed_idx = changed_spells.findIndex((c) => (spellId != null ? c.id === spellId : null))
  console.log('XXXXX changed_idx = ', changed_idx)
  //--------------------------------------------------------------------
  // find spell in original array (wont be an original entry without id)
  //--------------------------------------------------------------------
  const original_idx = original_spells.findIndex((o) => getSpellId(o) === spellId)
  //=========================================================================================================================
  // A. where spell NOT in change array + NOT in original array
  //---------------------------------------------------------------------------------------
  if (changed_idx == -1 && original_idx == -1) {
    //---------------------------------------------
    // deleting a persisted spell not found in the
    // local original snapshot: record DELETE, not ADD
    //---------------------------------------------
    if (del) {
      if (spellId != null && !hasTempId(spellId)) {
        console.log('A0. PERSISTED SPELL DELETE WITHOUT ORIGINAL MATCH - CREATE DELETION ENTRY')
        spell_changed_parm = createChangedSpellEntry({
          name: spell.name,
          action: 'DELETE',
          type: endpointType,
          id: spellId,
          ...getSpellRuneMetadata(spell),
        })
        changed_spells.push(spell_changed_parm)
      }

      console.log(
        '##################### UPDATED changed_spells = ',
        window.$deepCopy(changed_spells),
      )
      console.log('===================================================')
      return changed_spells
    }

    console.log('A. SPELL NOT IN CHANGED OR ORIGINAL ARRAY NAME = ', spell.name)
    //-------------------------------------------------------
    // static spell id passed from spell addition via SpellCard
    //-------------------------------------------------------
    spell_changed_parm = createChangedSpellEntry({
      name: spell.name,
      id: spellId, // real or temp id used for tracking
      action: 'ADD',
      type: endpointType, // character, item or rune (needed to determine which api call)
      ...getSpellRuneMetadata(spell),
      payload: {
        character: character_id ? character_id : null,
        spell: spell.spell ? spell.spell : null, // static spell id
        mag: spell.mag,
        character_item: spell.character_item,
        character_rune: spell.character_rune,
      },
    })
    changed_spells.push(spell_changed_parm)
    //=========================================================================================================================
    // B. where spell NOT in change array + IS in original array (deleting or updating existing character spell)
    //-----------------------------------------------------------------------------------------------------------
  } else if (changed_idx == -1 && original_idx !== -1) {
    console.log('B. SPELL NOT IN CHANGED ARRAY + IS IN ORIGINAL ARRAY: ' + spell.name)
    //--------------------------------
    // where deleting character spell
    //--------------------------------
    if (del) {
      console.log('B1. CREATE DELETION ENTRY')
      //--------------------
      // add deletion entry
      //--------------------
      spell_changed_parm = createChangedSpellEntry({
        name: spell.name,
        action: 'DELETE',
        type: endpointType, // character, item or rune (needed to determine which api call)
        id: spellId, // character_spell id or temp id
        ...getSpellRuneMetadata(spell),
      })
      //------------------
      // add update entry
      //------------------
    } else {
      console.log('B2. CREATE UPDATE ENTRY')
      spell_changed_parm = createChangedSpellEntry({
        name: spell.name,
        action: 'UPDATE',
        id: spellId, // character_spell id or temp id
        type: endpointType, // character, item or rune (needed to determine which api call)
        ...getSpellRuneMetadata(spell),
        payload: {
          [change.key]: change.value,
        },
      })
    }
    changed_spells.push(spell_changed_parm)
    //=========================================================================================================================
    // C. where spell IS in change array + IS in original array (updating existing change entry/remove if reverted)
    //--------------------------------------------------------------------------------------------------------------
  } else if (changed_idx !== -1 && original_idx !== -1) {
    console.log('C. SPELL IS IN CHANGED ARRAY + IS IN ORIGINAL ARRAY: ' + spell.name)
    console.log('C1. CHECK IF SPELL CHANGED')
    //--------------------------------
    // where deleting character spell
    //--------------------------------
    if (del) {
      console.log('C2. CREATE DELETION ENTRY')
      //---------------------------------
      // remove change entry where found
      //---------------------------------
      changed_spells.splice(changed_idx, 1)
      //--------------------
      // add deletion entry
      //--------------------
      spell_changed_parm = createChangedSpellEntry({
        name: spell.name,
        action: 'DELETE',
        id: spellId, // character_spell id or temp id
        type: endpointType, // character, item or rune (needed to determine which api call)
        ...getSpellRuneMetadata(spell),
      })
      changed_spells.push(spell_changed_parm)
      //--------------
      // not deleting
      //--------------
    } else {
      //-----------------------------------------------------------------
      // check if values changed (could have reverted back to originals)
      //-----------------------------------------------------------------
      let spell_changed = isSpellChanged(original_spells[original_idx], spell)
      //---------------
      // value changed
      //---------------
      if (spell_changed) {
        console.log('C3. SPELL VAL CHANGED - UPDATE ENTRY: ' + spell.name)
        //--------------------
        // update spell entry
        //--------------------
        changed_spells[changed_idx].payload[change.key] = change.value
        //---------------------------------------
        // value is original - remove from array
        //---------------------------------------
      } else {
        console.log('C4. SPELL NOT CHANGED - REMOVE ENTRY: ' + spell.name)
        changed_spells.splice(changed_idx, 1)
      }
    }
    //=========================================================================================================================
    // D. where spell IS in change array + NOT in original array (updating and add entry - further changes before commit
    //------------------------------------------------------------------------------------------------------------------
  } else {
    console.log('D. SPELL IS IN CHANGED ARRAY + NOT IN ORIGINAL ARRAY: ' + spell.name)
    //--------------------------------
    // where local spell being deleted
    //--------------------------------
    if (del) {
      console.log('D1. NEW SPELL BEING DELETED - REMOVE ENTRY: ' + spell.name)
      changed_spells.splice(changed_idx, 1)
    } else {
      console.log('D2. UPDATE THE UPDATE ENTRY')
      //-------------------
      // update spell entry
      //-------------------
      console.log(
        'D3. changed_spells[changed_idx] = ',
        window.$deepCopy(changed_spells[changed_idx]),
      )
      console.log('D4. change = ', change)
      changed_spells[changed_idx].payload[change.key] = change.value
    }
  }
  console.log('##################### UPDATED changed_spells = ', window.$deepCopy(changed_spells))
  console.log('===================================================')
  return changed_spells
}
